import { ChangeDetectionStrategy, Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { DemoDashboardId } from 'src/models';
import { DemoService } from '../../services/demo/demo.service';
import { SharedBannerComponent } from '../shared-banner/component';

@UntilDestroy()
@Component({
  selector: 'app-demo-data-banner',
  templateUrl: './demo-data-banner.component.html',
  styleUrls: ['./demo-data-banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DemoDataBannerComponent implements OnInit, OnChanges {
  isDemoDataVisible$: Observable<boolean>;

  /** Translated label (e.g. Team Dashboard). Pass from host: [dashboardName]="'dashboard.toolbar.team.title' | translate" */
  @Input() dashboardName?: string;

  /**
   * Pre-translated description. When set, overrides the default translated description.
   * Pass from host: [descriptionOverride]="'mockData.demoDataBanner.reportDescription' | translate"
   */
  @Input() descriptionOverride?: string;

  /** Which dashboard this banner is on; clearing sample data only affects this dashboard when using {@link DemoService}. */
  @Input() demoDashboardId?: DemoDashboardId;

  /**
   * When set (e.g. report-specific mock GrowthBook flag), drives visibility instead of {@link DemoService.showDemoData$}.
   */
  @Input() visibilityOverride$?: Observable<boolean>;

  /**
   * When set, invoked on clear instead of {@link DemoService.dismissDemoDataForDashboard}
   * (caller should call `banner.dismiss()` where appropriate).
   */
  @Input() customClearSampleData?: (banner: SharedBannerComponent) => void;

  bannerVisible$!: Observable<boolean>;

  /**
   * When set (e.g. Meeting Insights mock stream), overrides global {@link DemoService.showDemoData$} for visibility.
   */
  @Input() demoVisibility$?: Observable<boolean>;

  constructor(
    private demoService: DemoService,
    private readonly translate: TranslateService,
  ) {
    this.setVisibilityStream();
  }

  ngOnInit(): void {
    this.refreshBannerVisible();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['demoVisibility$']) {
      this.setVisibilityStream();
    }
    if (changes['visibilityOverride$']) {
      this.refreshBannerVisible();
    }
  }

  private setVisibilityStream(): void {
    const source$ = this.demoVisibility$ ?? this.demoService.showDemoData$;
    this.isDemoDataVisible$ = source$.pipe(
      map(isVisible => Boolean(isVisible)),
    );
  }

  private refreshBannerVisible(): void {
    this.bannerVisible$ = this.visibilityOverride$ ?? this.isDemoDataVisible$;
  }

  dismissDemoDataBanner(banner: SharedBannerComponent) {
    if (this.customClearSampleData) {
      this.customClearSampleData(banner);
      return;
    }
    if (this.demoDashboardId) {
      void this.demoService.dismissDemoDataForDashboard(this.demoDashboardId);
    }
    banner.dismiss();
  }

  get demoBannerParams(): { dashboardName: string } {
    const name = this.dashboardName?.trim()
      ? this.dashboardName
      : this.translate.instant('mockData.demoDataBanner.fallbackDashboardName');
    return { dashboardName: name };
  }
}
